import type { DefaultTheme } from 'styled-components';
import { getCulture } from 'src/shared/helpers';

export const getMadooEmbedLocale = () => {
    return getCulture() ?? 'en';
};

export const buildMadooBackofficeCss = (theme: DefaultTheme) => `
:root {
	--font-sans: ${theme.app.font};
	--background: ${theme.app.backgroundColorBase};
	--color-background: ${theme.app.backgroundColorBase};
	--foreground: ${theme.app.textColor};
	--color-foreground: ${theme.app.textColor};
	--card: ${theme.app.backgroundColor};
	--color-card: ${theme.app.backgroundColor};
	--card-foreground: ${theme.app.textColor};
	--color-card-foreground: ${theme.app.textColor};
	--popover: ${theme.app.backgroundColor};
	--color-popover: ${theme.app.backgroundColor};
	--popover-foreground: ${theme.app.textColor};
	--color-popover-foreground: ${theme.app.textColor};
	--primary: ${theme.app.secondary};
	--color-primary: ${theme.app.secondary};
	--primary-foreground: #ffffff;
	--color-primary-foreground: #ffffff;
	--secondary: ${theme.app.backgroundToolbar};
	--color-secondary: ${theme.app.backgroundToolbar};
	--secondary-foreground: ${theme.app.textColor};
	--color-secondary-foreground: ${theme.app.textColor};
	--muted: ${theme.app.pageBackgroundColor};
	--color-muted: ${theme.app.pageBackgroundColor};
	--muted-foreground: ${theme.app.primary};
	--color-muted-foreground: ${theme.app.primary};
	--accent: #fef2ed;
	--color-accent: #fef2ed;
	--accent-foreground: ${theme.app.secondary};
	--color-accent-foreground: ${theme.app.secondary};
	--border: ${theme.app.borderColor};
	--color-border: ${theme.app.borderColor};
	--input: ${theme.app.borderColor};
	--color-input: ${theme.app.borderColor};
	--ring: ${theme.app.secondary};
	--color-ring: ${theme.app.secondary};
	--radius: 8px;
}

body,
.madoo-embed,
.madoo-editor-embed {
	background: ${theme.app.backgroundColorBase};
	font-family: ${theme.app.font};
}
`;

export interface MadooRuntimeBridge {
    destroy(): void;
    isReady(): boolean;
    getIframe(): HTMLIFrameElement | null;
}

export interface MadooEditorBridge {
    save(requestId?: string): string;
    run(): void;
    setCustomCss(css: string): void;
    destroy(): void;
    isReady(): boolean;
    getIframe(): HTMLIFrameElement | null;
}

interface MadooEmbedSdk {
    create(options: {
        container: HTMLElement;
        iframeUrl: string;
        token?: string;
        locale?: string;
        theme?: {
            primaryColor?: string;
        };
        customCss?: string;
        width?: string;
        height?: string;
        allow?: string;
        showName?: boolean;
        showBranding?: boolean;
    }): MadooRuntimeBridge;
    editor: {
        create(options: {
            container: HTMLElement;
            editorUrl: string;
            token: string;
            workflowExternalId?: string;
            name?: string;
            locale?: string;
            theme?: {
                primaryColor?: string;
            };
            customCss?: string;
            width?: string;
            height?: string;
            allow?: string;
            showName?: boolean;
            showBranding?: boolean;
            onReady?: () => void;
            onSaved?: (payload: unknown) => void;
            onSaveFailed?: (payload: unknown) => void;
        }): MadooEditorBridge;
    };
}

declare global {
    interface Window {
        MadooEmbed?: MadooEmbedSdk;
    }
}

const sdkLoaders = new Map<string, Promise<MadooEmbedSdk>>();

export const loadMadooEmbedSdk = (sdkUrl: string): Promise<MadooEmbedSdk> => {
    if (!sdkUrl) return Promise.reject(new Error('Invalid Madoo embed SDK URL.'));
    if (window.MadooEmbed) return Promise.resolve(window.MadooEmbed);

    const existing = sdkLoaders.get(sdkUrl);
    if (existing) return existing;

    const loader = new Promise<MadooEmbedSdk>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = sdkUrl;
        script.async = true;
        script.onload = () => {
            if (window.MadooEmbed) resolve(window.MadooEmbed);
            else reject(new Error('Madoo embed SDK did not initialize.'));
        };
        script.onerror = () => reject(new Error('Unable to load Madoo embed SDK.'));
        document.head.appendChild(script);
    });

    sdkLoaders.set(sdkUrl, loader);
    return loader;
};
